import configService from '@/services/configService'
import { reactive, readonly } from 'vue'

// These values deliberately mirror the pre-branding UI.  Keeping them in one
// place means a partial organization theme can safely inherit the old design.
export const DEFAULT_WEBSITE_COLOR_THEME = Object.freeze({
  primary: '#570F8B',
  secondary: '#D19CFB',
  background: '#F6F8FF',
  surface: '#FFFFFF',
  text: '#333333',
  mutedText: '#8B8B8B',
  navbarBackground: '#570F8B',
  navbarText: '#FFFFFF',
  accent: '#57AD8D',
  border: '#3C4D6A',
  gradientStart: '#4F0ACF',
  gradientEnd: '#A404E1'
})

const themeVariables = {
  primary: '--primary-color',
  secondary: '--secondary-color',
  background: '--background-color',
  surface: '--surface-color',
  text: '--text-color',
  mutedText: '--muted-text-color',
  navbarBackground: '--navbar-background-color',
  navbarText: '--navbar-text-color',
  accent: '--accent-color',
  border: '--border-color',
  gradientStart: '--gradient-start',
  gradientEnd: '--gradient-end'
}

const brandingState = reactive({
  logoUrl: '',
  tagline: '',
  websiteColorTheme: { ...DEFAULT_WEBSITE_COLOR_THEME }
})

// Shared read-only state for any component that needs organization branding.
export const organizationBranding = readonly(brandingState)

const updateFavicon = (logoUrl) => {
  if (typeof document === 'undefined') return

  const iconUrl = logoUrl || '/favicon-small.png'
  const definitions = [
    { key: 'icon', rel: 'icon' },
    { key: 'shortcut', rel: 'shortcut icon' },
    { key: 'apple', rel: 'apple-touch-icon' }
  ]

  definitions.forEach(({ key, rel }) => {
    let link = document.head.querySelector(`link[data-organization-favicon="${key}"]`)
    if (!link) {
      link = document.createElement('link')
      link.dataset.organizationFavicon = key
      link.rel = rel
      document.head.appendChild(link)
    }
    link.href = iconUrl
  })
}

const isColor = (value) => {
  if (typeof value !== 'string' || !value.trim()) return false
  return typeof CSS === 'undefined' || typeof CSS.supports !== 'function' || CSS.supports('color', value.trim())
}

const isSafeImageUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return false
  try {
    const url = new URL(value.trim())
    return url.protocol === 'https:' || url.protocol === 'http:'
  } catch (_) {
    return false
  }
}

const unwrapConfig = (config) => {
  if (!config || typeof config !== 'object' || Array.isArray(config)) return {}
  return config.config || config.settings || config
}

export const normalizeOrganizationBranding = (config) => {
  const raw = unwrapConfig(config)
  const rawTheme = raw.websiteColorTheme && typeof raw.websiteColorTheme === 'object'
    ? raw.websiteColorTheme
    : {}

  const websiteColorTheme = Object.keys(DEFAULT_WEBSITE_COLOR_THEME).reduce((theme, key) => {
    theme[key] = isColor(rawTheme[key]) ? rawTheme[key].trim() : DEFAULT_WEBSITE_COLOR_THEME[key]
    return theme
  }, {})

  return {
    logoUrl: isSafeImageUrl(raw.logoUrl) ? raw.logoUrl.trim() : '',
    tagline: typeof raw.tagline === 'string' ? raw.tagline.trim() : '',
    websiteColorTheme
  }
}

export const applyOrganizationBranding = (branding, vuetify) => {
  const normalized = normalizeOrganizationBranding(branding)
  const raw = unwrapConfig(branding)
  const rawTheme = raw.websiteColorTheme && typeof raw.websiteColorTheme === 'object'
    ? raw.websiteColorTheme
    : {}
  const hasBranding = Object.keys(rawTheme).length > 0 || Boolean(raw.logoUrl || raw.tagline)
  const root = typeof document === 'undefined' ? null : document.documentElement

  if (root) {
    Object.entries(themeVariables).forEach(([key, variable]) => {
      root.style.setProperty(variable, normalized.websiteColorTheme[key])
    })

    // A branded tenant uses its flat background throughout the public and
    // signed-in shell. The legacy gradients remain only for unbranded use.
    const loginBackground = hasBranding
      ? normalized.websiteColorTheme.background
      : 'linear-gradient(135deg, var(--gradient-start), var(--gradient-end))'
    root.style.setProperty('--login-background', loginBackground)
    root.style.setProperty(
      '--main-content-overlay',
      hasBranding ? 'transparent' : 'linear-gradient(135deg, var(--gradient-start), var(--gradient-end))'
    )

    if (hasBranding) {
      root.dataset.organizationBranding = 'true'
    } else {
      delete root.dataset.organizationBranding
    }
  }

  // Vuetify colors are maintained separately from CSS custom properties.
  // Update its reactive theme definition so v-* components change with the app.
  const colors = vuetify?.theme?.themes?.value?.customTheme?.colors
  if (colors) {
    Object.assign(colors, {
      primary: normalized.websiteColorTheme.primary,
      secondary: normalized.websiteColorTheme.secondary,
      background: normalized.websiteColorTheme.background,
      surface: normalized.websiteColorTheme.surface,
      text: normalized.websiteColorTheme.text,
      error: '#ff3e3e'
    })
  }

  Object.assign(brandingState, normalized)
  updateFavicon(normalized.logoUrl)

  return normalized
}

/**
 * Load tenant branding before mounting the UI. Branding is intentionally
 * opt-in: without VUE_APP_ORGANIZATION_ID the current product design remains
 * untouched and no public configuration request is made. The environment flag
 * enables branding; organization resolution is performed by the public API.
 */
export const initializeOrganizationBranding = async ({ organizationId, vuetify } = {}) => {
  if (!organizationId || !String(organizationId).trim()) {
    return applyOrganizationBranding({}, vuetify)
  }

  try {
    const config = await configService.getPublicConfig()
    return applyOrganizationBranding(config, vuetify)
  } catch (error) {
    // Branding must never prevent access to the application. The defaults are
    // applied on every failure, including a temporarily unavailable endpoint.
    console.warn('Unable to load organization branding; using default design.', error)
    return applyOrganizationBranding({}, vuetify)
  }
}
